import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Import pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import StudentDashboard from "./pages/StudentDashboard";
import CourseCatalog from "./pages/CourseCatalog";
import CourseDetail from "./pages/CourseDetail";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCourses from "./pages/AdminCourses";
import AdminUsers from "./pages/AdminUsers";
import AdminNotifications from "./pages/AdminNotifications";
import AdminPromoCodes from "./pages/AdminPromoCodes";
import AdminFreeAccess from "./pages/AdminFreeAccess";
import AdminSettings from "./pages/AdminSettings";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import AdminRefunds from "./pages/AdminRefunds";
import AdminBulkEnroll from "./pages/AdminBulkEnroll";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminCustomize from "./pages/SuperAdminCustomize";
import Certificate from "./pages/Certificate";
import VerifyCertificate from "./pages/VerifyCertificate";
import MyCertificates from "./pages/MyCertificates";
import Register from "./pages/Register";
import PaymentSuccess from "./pages/PaymentSuccess";
import SetPassword from "./pages/SetPassword";
import ExploreCourses from "./pages/ExploreCourses";
import CourseInfo from "./pages/CourseInfo";
import MySubscription from "./pages/MySubscription";
import MyProgress from "./pages/MyProgress";
import ForgotPassword from "./pages/ForgotPassword";
import AdminCertificateRequests from "./pages/AdminCertificateRequests";

// Push Notifications
import PushConsentModal from "./components/PushConsentModal";
import usePushNotifications from "./hooks/usePushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, {
      withCredentials: true
    });
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, {
      withCredentials: true
    });
    setUser(response.data);
    return response.data;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
      withCredentials: true
    });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, googleLogin, processSession, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false, superadminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (superadminOnly && user.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOnly && !['admin', 'superadmin'].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Push Notification Context
export const PushNotificationContext = createContext(null);

export const usePush = () => {
  const context = useContext(PushNotificationContext);
  return context; // Can be null if not in authenticated context
};

// Push Notification Provider - wraps authenticated content
const PushNotificationProvider = ({ children }) => {
  const pushNotifications = usePushNotifications();
  const { user } = useAuth();
  
  // Only track engagement when user is logged in
  useEffect(() => {
    if (user && pushNotifications.status.isSupported) {
      // Track dashboard visit on mount (for routes under this provider)
      pushNotifications.trackEngagement('dashboard_visit');
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return (
    <PushNotificationContext.Provider value={pushNotifications}>
      {children}
      {/* Push consent modal - shown based on eligibility */}
      <PushConsentModal
        isOpen={pushNotifications.showConsentModal}
        onClose={() => pushNotifications.setShowConsentModal(false)}
        onConsent={pushNotifications.handleConsent}
      />
    </PushNotificationContext.Provider>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL hash (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/explore-courses" element={<ExploreCourses />} />
      <Route path="/course/:courseId" element={<CourseInfo />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/verify/:certificateNumber" element={<VerifyCertificate />} />
      <Route path="/verify" element={<VerifyCertificate />} />
      <Route path="/certificate/:certificateId" element={<Certificate />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/courses" element={
        <ProtectedRoute>
          <CourseCatalog />
        </ProtectedRoute>
      } />
      <Route path="/courses/:courseId" element={
        <ProtectedRoute>
          <CourseDetail />
        </ProtectedRoute>
      } />
      <Route path="/certificates" element={
        <ProtectedRoute>
          <MyCertificates />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <MySubscription />
        </ProtectedRoute>
      } />
      <Route path="/progress" element={
        <ProtectedRoute>
          <MyProgress />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses" element={
        <ProtectedRoute adminOnly>
          <AdminCourses />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute adminOnly>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/notifications" element={
        <ProtectedRoute adminOnly>
          <AdminNotifications />
        </ProtectedRoute>
      } />
      <Route path="/admin/promo-codes" element={
        <ProtectedRoute adminOnly>
          <AdminPromoCodes />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute adminOnly>
          <AdminFreeAccess />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute adminOnly>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/subscriptions" element={
        <ProtectedRoute adminOnly>
          <AdminSubscriptions />
        </ProtectedRoute>
      } />
      <Route path="/admin/refunds" element={
        <ProtectedRoute adminOnly>
          <AdminRefunds />
        </ProtectedRoute>
      } />
      <Route path="/admin/bulk-enroll" element={
        <ProtectedRoute adminOnly>
          <AdminBulkEnroll />
        </ProtectedRoute>
      } />
      <Route path="/admin/certificate-requests" element={
        <ProtectedRoute adminOnly>
          <AdminCertificateRequests />
        </ProtectedRoute>
      } />
      <Route path="/superadmin" element={
        <ProtectedRoute superadminOnly>
          <SuperAdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/superadmin/customize" element={
        <ProtectedRoute superadminOnly>
          <SuperAdminCustomize />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <PushNotificationProvider>
          <AppRouter />
        </PushNotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
